import { useFormContext } from 'react-hook-form';
import { OkButton } from 'src/components/Buttons/OkButton';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { Button, Tooltip } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import {
  AutoPricingCompListingMode,
  AutoPricingOutlierMode,
  AutoPricingUndercutMode,
  Feature,
  ListingGroupAutoPriceSettings,
  UiCurrency,
} from 'src/WebApiController';

import { CompListingPrices } from './CompListingPrices';
import { OutlierSettings } from './OutlierSettings';
import * as styles from './PricingSetting.css';
import { PricingSettingAutoPriceFloorCeilingInput } from './PricingSettingAutoPriceFloorCeilingInput';
import { PricingSettingAutoPriceRankPremiumSettings } from './PricingSettingAutoPriceRankPremiumSettings';
import { PricingSettingManualPriceRankPremiumSettings } from './PricingSettingManualPriceRankPremiumSettings';

export const PricingSettingAutoPriceBody = ({
  sellerFee,
  uiCurrency,
  hasChanges,
  hasSelectedSections,
  isLoading,
  isListingGroupForm,
  isPriceStaggerEnabled,
  useVerticalView,
  onReset,
  onSubmit,
}: {
  undercutRelativeAmount: number | null;
  undercutAbsoluteAmount: number | null;
  compListingMode: AutoPricingCompListingMode | null;
  outlierMode: AutoPricingOutlierMode | null;
  undercutMode: AutoPricingUndercutMode | null;
  outlierStandardDeviations: number | null;
  outlierKthLowestLimit: number | null;
  outlierKthLowestLimitAbsoluteSpacing: number | null;
  outlierKthLowestLimitRelativeSpacing: number | null;
  uiCurrency: UiCurrency;
  sellerFee: number | null;
  hasChanges: boolean;
  hasSelectedSections: boolean;
  isLoading: boolean;
  isListingGroupForm?: boolean | null;
  isPriceStaggerEnabled: boolean | null;
  useVerticalView: boolean;
  onReset: () => void;
  onSubmit: () => void;
}) => {
  const { watch } = useFormContext<ListingGroupAutoPriceSettings>();

  const autoPricingEnabled = watch('autoPricingEnabled');

  const hasAutoPricingEmptySelectedSectionsNoCompsFeature = useUserHasFeature(
    Feature.AutoPricingEmptySelectedSectionsNoComps
  );

  const hasManualPriceStaggersFeature = useUserHasFeature(
    Feature.ManualPriceStaggers
  );

  const resetText = useContent(ContentId.Reset);
  const { isDarkMode } = useSiteTheme();

  return (
    <div className={styles.pricingSettingsBodyInnerLayout}>
      <div className={styles.autoPriceSettingsContainer}>
        <div className={styles.settingsWrapper}>
          {autoPricingEnabled && (
            <CompListingPrices
              useVerticalView={useVerticalView}
              uiCurrency={uiCurrency}
            />
          )}

          <>
            {!isPriceStaggerEnabled && (
              <PricingSettingAutoPriceFloorCeilingInput
                uiCurrency={uiCurrency}
                sellerFee={sellerFee}
                disabled={isLoading}
              />
            )}

            {isListingGroupForm && (
              <>
                {!autoPricingEnabled && hasManualPriceStaggersFeature && (
                  <PricingSettingManualPriceRankPremiumSettings
                    uiCurrency={uiCurrency}
                    sellerFee={sellerFee}
                  />
                )}

                <PricingSettingAutoPriceRankPremiumSettings
                  uiCurrency={uiCurrency}
                />
              </>
            )}
          </>
        </div>

        {autoPricingEnabled && (
          <OutlierSettings useVerticalView={useVerticalView} />
        )}
      </div>
      <div className={styles.innerLayoutFooter}>
        <Button
          variant="text"
          textColor="primary"
          justifyContent="start"
          fontWeight={400}
          onClick={onReset}
          shape="none"
        >
          {resetText}
        </Button>
        {!hasSelectedSections &&
        !isLoading &&
        hasChanges &&
        !isListingGroupForm &&
        hasAutoPricingEmptySelectedSectionsNoCompsFeature ? (
          <Tooltip
            triggerContent={
              <div style={{ display: 'inline-flex', pointerEvents: 'auto' }}>
                <OkButton
                  disabled={isLoading || !hasChanges || !hasSelectedSections}
                  onClick={onSubmit}
                  textContentId={ContentId.Save}
                />
              </div>
            }
            align="center"
            delayDuration={0}
            contentVariant={isDarkMode ? 'light' : 'dark'}
          >
            <Content id={ContentId.SelectAtLeastOneSection} />
          </Tooltip>
        ) : (
          <OkButton
            disabled={
              isLoading ||
              !hasChanges ||
              (hasAutoPricingEmptySelectedSectionsNoCompsFeature &&
                !hasSelectedSections &&
                !isListingGroupForm)
            }
            onClick={onSubmit}
            textContentId={ContentId.Save}
          />
        )}
      </div>
    </div>
  );
};
